// src/pages/onboarding/OnboardingLayout.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

import WelcomeStep from "./WelcomeStep";
import ProfileStep from "./ProfileStep";
import CompletionStep from "./CompletionStep";
import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from "../../config";

const steps = ["Welcome", "Profile", "Ready"];

export default function OnboardingLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleComplete = async () => {
    // The backend is the source of truth: every login overwrites the stored
    // user with the server's payload. Marking this done locally while the call
    // failed is what sent people back through onboarding on every login, so
    // persist first and only then trust the flag.
    setSaving(true);
    setError("");

    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      if (!token) {
        navigate("/login", { replace: true });
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.COMPLETE_ONBOARDING}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // fetch only rejects on network errors, so a 4xx/5xx has to be checked.
      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }

      const data = await response.json();

      const existingUser = localStorage.getItem(STORAGE_KEYS.USER);
      const userData = existingUser ? JSON.parse(existingUser) : {};
      userData.onboarding_completed = data?.onboarding_completed ?? true;
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));

      navigate("/app", { replace: true });
    } catch (err) {
      console.error("Could not complete onboarding:", err);
      setError(
        "We couldn't finish setting up your account. Please try again — if it keeps happening, contact support."
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <WelcomeStep onNext={handleNext} />;
      case 1:
        return <ProfileStep onNext={handleNext} onBack={handleBack} />;
      case 2:
        return (
          <CompletionStep
            onComplete={handleComplete}
            saving={saving}
            error={error}
          />
        );
      default:
        return <WelcomeStep onNext={handleNext} />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f5f7fa",
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 700,
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
        }}
      >
        {/* Header with logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 4,
          }}
        >
          <BoltRoundedIcon sx={{ color: "primary.main", fontSize: 28 }} />
          <Typography variant="h6" fontWeight={800}>
            AgentBlayna
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step content */}
        <Box sx={{ minHeight: 400 }}>{renderStep()}</Box>
      </Paper>
    </Box>
  );
}
